import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './Reports.css'
import { GdkPillFilter } from '@gdk/pill-filter';
import { GdkModal } from '@gdk/modal';
import DatePicker from './DatePicker';

export default function ReportDateFilters({
    applyFilters = () => { },
    selectedFilter,
    setSelectedFilter = () => { },
    allInStartDate = null,
    allInEndDate = null,
    customStartDate = null,
    customEndDate = null,
    sweeps = false,
}) {
    const [startDate, setStartDate] = useState(customStartDate);
    const [endDate, setEndDate] = useState(customEndDate);
    const [isCustomSelected, setIsCustomSelected] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const customFilterPillRef = useRef(null);
    const allInFilterPillRef = useRef(null);
    const gdkPillFilterRef = useRef();
    const gdkModalRef = useRef(null);
    const modalIdRef = useRef("custom-filter-modal");

    const dateFilterOptions = sweeps
        ? ["1 day", "3 days", "7 days", "30 days", "All In", "Custom"]
        : ["T1", "T3", "T6", "T12", "YTD", "All In", "Custom"];

    useEffect(() => {
        gdkPillFilterRef.current = new GdkPillFilter({ content: "#reports-pill-filters" });
    }, []);

    useEffect(() => {
        gdkModalRef.current = new GdkModal({
            content: "#" + modalIdRef.current,
            hideCloseButton: true,
            onClosed: () => {
                setShowModal(false);
            },
        });
    }, []);

    useEffect(() => {
        if (showModal) {
            gdkModalRef.current.show();
        } else {
            gdkModalRef.current.hide();
        }
    }, [showModal]);

    const startDateOffsets = {
        T1: { months: 1 }, T3: { months: 3 }, T6: { months: 6 }, T12: { years: 1 },
        "1 day": { days: 1 }, "3 days": { days: 3 }, "7 days": { days: 7 }, "30 days": { days: 30 }
    };

    function setFilter(filter) {
        if (filter === selectedFilter) { return }

        if (filter === "All In") {
            applyFilters({});
        } else {
            let filterStartDate;
            if (filter === "Custom") {
                filterStartDate = startDate;
            } else if (filter === "YTD") {
                filterStartDate = new Date(allInEndDate.getFullYear(), 0, 1);
            } else {
                filterStartDate = calculateDateOffset(startDateOffsets[filter]);
            }

            applyFilters({
                startDate: filterStartDate,
                endDate: filter === "Custom" ? endDate : allInEndDate,
            });
        }
        setSelectedFilter(filter);
    }

    function calculateDateOffset({ days = 0, months = 0, years = 0 }) {
        const date = new Date(allInEndDate);
        date.setDate(date.getDate() - days);
        date.setMonth(date.getMonth() - months);
        date.setFullYear(date.getFullYear() - years);
        return date;
    }

    function getFilterDatesString(filter) {
        if (!allInEndDate) return '';
        switch (filter) {
            case "YTD":
                return `${new Date(allInEndDate.getFullYear(), 0, 1).toLocaleDateString()} - ${allInEndDate.toLocaleDateString()}`;
            case "Custom":
                return startDate && endDate ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : '';
            case "All In":
                return allInStartDate && allInEndDate ? `${allInStartDate.toLocaleDateString()} - ${allInEndDate.toLocaleDateString()}` : '';
            default:
                return `${calculateDateOffset(startDateOffsets[filter]).toLocaleDateString()} - ${allInEndDate.toLocaleDateString()}`;
        }
    }

    function selectCustomFilter(selected) {
        if (selected !== isCustomSelected) {
            customFilterPillRef.current.click();
        }
    }

    useEffect(() => {
        const customFilterPill = customFilterPillRef.current;
        if (customFilterPill) {
            const handleClick = () => setIsCustomSelected(!isCustomSelected);
            customFilterPill.addEventListener("click", handleClick);

            return () => {
                customFilterPill.removeEventListener("click", handleClick);
            };
        }

        const allInFilterPill = allInFilterPillRef.current;
        if (allInFilterPill && selectedFilter === "All In") {
            allInFilterPillRef.current.click();
        }
    }, [isCustomSelected, selectedFilter]);

    function onCancel() {
        setShowModal(false);
    }

    async function onSubmit() {
        setFilter("Custom");
        selectCustomFilter(true);
        setShowModal(false);
    }

    function customFiltersModal() {
        return (
            <div id="custom-filter-modal" className="modal">
                <div className="modal-container" style={{ width: '30%', minWidth: '400px' }}>
                    <h4 className="modal-headline"> Select your date range </h4>
                    <div className="modal-content modal-content-with-cta-bar">
                        <label htmlFor="report-start-date" className="text">Start Date:</label>
                        <DatePicker
                            id="report-start-date"
                            minDate={allInStartDate}
                            maxDate={allInEndDate}
                            onDateSelected={(e) => setStartDate(new Date(e))}
                        />
                        <label htmlFor="report-end-date" className="text">End Date:</label>
                        <DatePicker
                            id="report-end-date"
                            minDate={allInStartDate}
                            maxDate={allInEndDate}
                            onDateSelected={(e) => setEndDate(new Date(e))}
                        />
                    </div>
                    <div className="modal-content">
                        <div className="modal--call-to-action-bar">
                            <button type="button" disabled={!startDate || !endDate} className="btn btn--primary btn--full-mobile btn--pull-right" onClick={onSubmit}>
                                <span>Submit</span>
                            </button>
                            <button type="button" className="btn btn--secondary btn--full-mobile btn--pull-right" onClick={onCancel}>
                                <span>Cancel</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {customFiltersModal()}
            <div id="reports-pill-filters" className="pill-filter">
                <p> Date Range: </p>
                {dateFilterOptions.map((filter) => {
                    const isSelected = selectedFilter === filter;
                    const filterDatesString = isSelected ? `: ${getFilterDatesString(filter)}` : '';

                    const handleClick = () => {
                        if (filter === "Custom") {
                            setShowModal(true);
                            customFilterPillRef.current.click();
                        } else {
                            setFilter(filter);
                        }
                    };

                    return (
                        <button
                            key={filter}
                            id={`${filter}-pill-filter`}
                            type="button"
                            className={`btn btn--pill-filter single-choice ${isSelected ? 'btn--selected' : ''}`}
                            ref={filter === "Custom" ? customFilterPillRef : filter === "All In" ? allInFilterPillRef : null}
                            onClick={handleClick}
                        >
                            {filter}{filter === "Custom" && isSelected && !showModal && startDate && endDate ? `: ${getFilterDatesString(filter)}` : filterDatesString}
                        </button>
                    );
                })}
            </div>
        </>
    );
}

ReportDateFilters.propTypes = {
    applyFilters: PropTypes.func,
    selectedFilter: PropTypes.string,
    setSelectedFilter: PropTypes.func,
    allInStartDate: PropTypes.instanceOf(Date),
    allInEndDate: PropTypes.instanceOf(Date),
    customStartDate: PropTypes.instanceOf(Date),
    customEndDate: PropTypes.instanceOf(Date),
    sweeps: PropTypes.bool,
};